import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaPlus, FaTrash, FaChevronUp, FaChevronDown } from "react-icons/fa";
import { API_BASE } from "../../../../ApiUrl";
import {
  COUNTRY_OPTIONS,
  createEmptyEmployment,
  createEmptyProject,
  createEmptyClientApproval,
  APPROVAL_STATUS_OPTIONS,
} from "../constants";

const Tab3Experience = ({
  employmentRecords,
  setEmploymentRecords,
  projectRecords,
  setProjectRecords,
  clientApprovalRecords,
  setClientApprovalRecords,
  projectOptions = [],
  errors,
  loading,
  addRecord,
  removeRecord,
  handleRecordChange,
  handleRecordFileChange,
  removeEmploymentRecord,
  removeProjectRecord,
  onSubmitEmployment,
  onSubmitProject,
  onSubmit,
  onBack,
}) => {
  const [industries, setIndustries] = useState([]);
  const [scopeCatalogs, setScopeCatalogs] = useState([]);
  const [scopeModules, setScopeModules] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [professionalRoles, setProfessionalRoles] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    employment: true,
    project: true,
    clientApproval: true,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const fetchCatalogData = async () => {
      try {
        const [
          referenceResponse,
          scopeCatalogResponse,
          scopeModuleResponse,
          formFieldResponse,
        ] = await Promise.all([
          axios.get(`${API_BASE}/reference-values/`),
          axios.get(`${API_BASE}/scope-catalogs/`),
          axios.get(`${API_BASE}/scope-modules/`),
          axios.get(`${API_BASE}/form-fields/`),
        ]);

        const referenceData = referenceResponse.data?.data || [];
        setIndustries(referenceData);

        const roles = referenceData.filter(
          (item) =>
            item.option_set_value?.option_type === "PROFESSIONAL_ROLE" ||
            item.option_set?.option_type === "PROFESSIONAL_ROLE",
        );
        setProfessionalRoles(roles);

        setScopeCatalogs(scopeCatalogResponse.data?.data || []);
        setScopeModules(scopeModuleResponse.data?.data || []);
        setFormFields(formFieldResponse.data?.data || []);
      } catch (error) {
        console.error("Error fetching catalog data", error);
      }
    };

    fetchCatalogData();
  }, []);

  const getIndustryOptions = () =>
    industries.filter(
      (item) =>
        item.option_set_value?.option_type === "INDUSTRY" ||
        item.option_set?.option_type === "INDUSTRY",
    );

  const getIndustryLabel = (industryId) => {
    const selection = industries.find((item) => item.id === industryId);
    return selection ? selection.label : "";
  };

  const getScopesForIndustry = (industryId) =>
    scopeCatalogs.filter(
      (scope) =>
        scope.industry === industryId || scope.industry?.id === industryId,
    );

  const getScopeModulesForScope = (scopeId) =>
    scopeModules.filter(
      (module) => module.scope === scopeId || module.scope?.id === scopeId,
    );

  const getFieldsForScope = (scopeId) => {
    const moduleIds = getScopeModulesForScope(scopeId).map(
      (module) => module.form_module || module.form_module?.id,
    );
    return formFields
      .filter((field) =>
        moduleIds.includes(field.form_module || field.form_module?.id),
      )
      .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  };

  const getScopeLabel = (scopeId) => {
    const selection = scopeCatalogs.find((item) => item.id === scopeId);
    return selection ? selection.scope_name : "";
  };

  const getScopeFieldDefaultValue = (field) => {
    const uiControl = (field.ui_control || "").trim().toLowerCase();
    const dataType = (field.data_type || "").trim().toLowerCase();

    if (
      dataType === "enum_list" ||
      dataType === "string_list" ||
      uiControl.includes("multi")
    ) {
      return [];
    }
    if (dataType === "boolean" || uiControl.includes("checkbox")) {
      return false;
    }
    return "";
  };

  // ✅ CHANGED: use fieldId (numeric id) instead of fieldCode
  const getScopeFieldValue = (record, scopeId, fieldId, groupIndex) => {
    const key = `${scopeId}:${fieldId}`;
    const stored = record.scope_field_values?.[key];
    if (Array.isArray(stored) && typeof groupIndex === "number") {
      return stored[groupIndex] ?? "";
    }
    return stored ?? "";
  };

  // ✅ CHANGED: use fieldId
  const getScopeFieldValueArray = (record, scopeId, fieldId, field) => {
    const key = `${scopeId}:${fieldId}`;
    const stored = record.scope_field_values?.[key];
    if (Array.isArray(stored)) {
      return stored;
    }
    if (stored !== undefined && stored !== "") {
      return [stored];
    }
    return [getScopeFieldDefaultValue(field)];
  };

  // ✅ CHANGED: use fieldId
  const updateScopeFieldValues = (
    record,
    scopeId,
    fieldId,
    value,
    groupIndex,
  ) => {
    const key = `${scopeId}:${fieldId}`;
    const values = { ...record.scope_field_values };

    if (typeof groupIndex === "number") {
      const existing = Array.isArray(values[key]) ? [...values[key]] : [];
      existing[groupIndex] = value;
      values[key] = existing;
    } else {
      values[key] = value;
    }

    return values;
  };

  const handleIndustryChange = (index, industryId) => {
    const numericIndustryId = industryId ? Number(industryId) : null;
    setProjectRecords((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        industry_id: numericIndustryId,
        industry_label: getIndustryLabel(numericIndustryId),
        scope_ids: [],
        scope_field_values: {},
      };
      return updated;
    });
  };

  const handleScopeToggle = (index, scopeId, checked) => {
    setProjectRecords((prev) => {
      const updated = [...prev];
      const record = { ...updated[index] };
      const scopeIds = Array.isArray(record.scope_ids)
        ? [...record.scope_ids]
        : [];

      if (checked) {
        record.scope_ids = [...new Set([...scopeIds, scopeId])];
      } else {
        record.scope_ids = scopeIds.filter((id) => id !== scopeId);
        const values = { ...record.scope_field_values };
        Object.keys(values).forEach((key) => {
          if (key.startsWith(`${scopeId}:`)) {
            delete values[key];
          }
        });
        record.scope_field_values = values;
      }

      updated[index] = record;
      return updated;
    });
  };

  // ✅ CHANGED: parameter renamed to fieldId
  const handleScopeFieldChange = (
    index,
    scopeId,
    fieldId,
    value,
    groupIndex,
  ) => {
    setProjectRecords((prev) => {
      const updated = [...prev];
      const record = { ...updated[index] };
      record.scope_field_values = updateScopeFieldValues(
        record,
        scopeId,
        fieldId,
        value,
        groupIndex,
      );
      updated[index] = record;
      return updated;
    });
  };

  // ✅ CHANGED: use field.id
  const addRepeatableScopeFieldEntry = (index, scopeId, field) => {
    setProjectRecords((prev) => {
      const updated = [...prev];
      const record = { ...updated[index] };
      const currentValues = getScopeFieldValueArray(
        record,
        scopeId,
        field.id,
        field,
      );
      const nextValues = [...currentValues, getScopeFieldDefaultValue(field)];
      record.scope_field_values = {
        ...record.scope_field_values,
        [`${scopeId}:${field.id}`]: nextValues,
      };
      updated[index] = record;
      return updated;
    });
  };

  // ✅ CHANGED: use field.id
  const removeRepeatableScopeFieldEntry = (
    index,
    scopeId,
    field,
    groupIndex,
  ) => {
    setProjectRecords((prev) => {
      const updated = [...prev];
      const record = { ...updated[index] };
      const key = `${scopeId}:${field.id}`;
      const currentValues = Array.isArray(record.scope_field_values?.[key])
        ? [...record.scope_field_values[key]]
        : [];

      currentValues.splice(groupIndex, 1);
      record.scope_field_values = {
        ...record.scope_field_values,
        [key]: currentValues,
      };
      updated[index] = record;
      return updated;
    });
  };

  // ✅ CHANGED: use field.id everywhere
  const renderScopeFieldInput = (record, index, scopeId, field, groupIndex) => {
    const fieldValue = getScopeFieldValue(
      record,
      scopeId,
      field.id,
      groupIndex,
    );
    const uiControl = (field.ui_control || "").trim().toLowerCase();
    const dataType = (field.data_type || "").trim().toLowerCase();
    const options = Array.isArray(field.options_schema)
      ? field.options_schema
      : [];
    const baseClass = "register-stage2-form__input";
    const handleValue = (value) =>
      handleScopeFieldChange(
        index,
        scopeId,
        field.id,
        value,
        field.is_repeatable ? groupIndex : undefined,
      );

    if (uiControl.includes("textarea") || dataType === "rich_text") {
      return (
        <textarea
          className="register-stage2-form__textarea"
          rows="2"
          value={fieldValue}
          onChange={(e) => handleValue(e.target.value)}
          placeholder={field.field_label}
          disabled={loading}
        />
      );
    }

    if (
      uiControl.includes("multi") ||
      dataType === "enum_list" ||
      dataType === "string_list"
    ) {
      const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
      return (
        <div className="register-stage2-multiselect-grid">
          {options.map((option) => {
            const optionValue =
              typeof option === "object" ? option.value : option;
            const optionLabel =
              typeof option === "object"
                ? option.label || option.value
                : option;
            const checked = selectedValues.includes(optionValue);
            return (
              <label
                key={optionValue}
                className="register-stage2-multiselect-option"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  value={optionValue}
                  onChange={(e) => {
                    const nextValues = checked
                      ? selectedValues.filter((value) => value !== optionValue)
                      : [...selectedValues, optionValue];
                    handleValue(nextValues);
                  }}
                  disabled={loading}
                />
                <span>{optionLabel}</span>
              </label>
            );
          })}
        </div>
      );
    }

    if (
      uiControl.includes("select") ||
      uiControl.includes("dropdown") ||
      dataType === "enum"
    ) {
      const optionItems =
        Array.isArray(options) && options.length > 0 ? options : [];
      return (
        <select
          className="register-stage2-form__select"
          value={fieldValue || ""}
          onChange={(e) => handleValue(e.target.value)}
          disabled={loading}
        >
          <option value="">Select {field.field_label}</option>
          {optionItems.map((option) => {
            const optionValue =
              typeof option === "object" ? option.value : option;
            const optionLabel =
              typeof option === "object"
                ? option.label || option.value
                : option;
            return (
              <option key={optionValue} value={optionValue}>
                {optionLabel}
              </option>
            );
          })}
        </select>
      );
    }

    if (uiControl.includes("date") || dataType === "date") {
      return (
        <input
          type="date"
          className={baseClass}
          value={fieldValue}
          onChange={(e) => handleValue(e.target.value)}
          disabled={loading}
        />
      );
    }

    if (
      uiControl.includes("number") ||
      dataType === "integer" ||
      dataType === "decimal"
    ) {
      return (
        <input
          type="number"
          className={baseClass}
          value={fieldValue}
          onChange={(e) => handleValue(e.target.value)}
          disabled={loading}
        />
      );
    }

    if (uiControl.includes("checkbox") || dataType === "boolean") {
      return (
        <input
          type="checkbox"
          checked={Boolean(fieldValue)}
          onChange={(e) => handleValue(e.target.checked)}
          disabled={loading}
        />
      );
    }

    return (
      <input
        type="text"
        className={baseClass}
        value={fieldValue}
        onChange={(e) => handleValue(e.target.value)}
        placeholder={field.field_label}
        disabled={loading}
      />
    );
  };

  // ✅ CHANGED: use field.id
  const renderScopeField = (record, index, scopeId, field) => {
    if (!field.is_repeatable) {
      return renderScopeFieldInput(record, index, scopeId, field);
    }

    const values = getScopeFieldValueArray(record, scopeId, field.id, field);
    return (
      <div className="register-stage2-repeatable-field">
        {values.map((_, groupIndex) => (
          <div
            key={`${scopeId}:${field.id}:${groupIndex}`}
            className="register-stage2-repeatable-field__row"
          >
            <div className="register-stage2-repeatable-field__input-wrapper">
              <span className="register-stage2-repeatable-field__index">
                {groupIndex + 1}
              </span>
              {renderScopeFieldInput(record, index, scopeId, field, groupIndex)}
              <button
                type="button"
                className="register-stage2-form__btn register-stage2-form__btn--outline register-stage2-form__btn--small register-stage2-repeatable-field__remove"
                onClick={() =>
                  removeRepeatableScopeFieldEntry(
                    index,
                    scopeId,
                    field,
                    groupIndex,
                  )
                }
                disabled={loading || values.length <= 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="register-stage2-add-btn register-stage2-add-btn--small"
          onClick={() => addRepeatableScopeFieldEntry(index, scopeId, field)}
          disabled={loading}
        >
          + Add another
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Section 9: Employment History */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("employment")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">🏢</span>
              9. Employment History
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.employment ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Chronological employment history.
          </p>
        </div>

        {expandedSections.employment && (
          <>
            {errors.employment && (
              <span className="register-stage2-form__error-text register-stage2-form__error-text--block">
                {errors.employment}
              </span>
            )}

            {employmentRecords.map((record, index) => (
              <div
                key={record.id}
                className="register-stage2-record-card register-stage2-record-card--compact"
              >
                <div className="register-stage2-record-card__header">
                  <h4 className="register-stage2-record-card__title">
                    Employment #{index + 1}
                  </h4>
                  {employmentRecords.length > 1 && (
                    <button
                      type="button"
                      className="register-stage2-record-card__remove"
                      onClick={() => removeEmploymentRecord(index)}
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <div className="register-stage2-form__grid register-stage2-form__grid--4col">
                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Employer name
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.employer}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "employer",
                          e.target.value,
                        )
                      }
                      placeholder="Employer Name"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">Job title</label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.title}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "title",
                          e.target.value,
                        )
                      }
                      placeholder="Job Title"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Employment type
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.employment_type}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "employment_type",
                          e.target.value,
                        )
                      }
                      placeholder="Employment Type"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Location — city
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.location}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "location",
                          e.target.value,
                        )
                      }
                      placeholder="City"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Location — country
                    </label>
                    <select
                      className="register-stage2-form__select"
                      value={record.country}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "country",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    >
                      <option value="">Country</option>
                      {COUNTRY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Start date
                    </label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.start_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "start_date",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">End date</label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.end_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setEmploymentRecords,
                          index,
                          "end_date",
                          e.target.value,
                        )
                      }
                      placeholder="End date"
                      disabled={loading || record.is_current}
                    />
                    <small className="register-stage2-form__help-text">
                      Blank only if current
                    </small>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={record.is_current}
                        onChange={(e) =>
                          handleRecordChange(
                            setEmploymentRecords,
                            index,
                            "is_current",
                            e.target.checked,
                          )
                        }
                        disabled={loading}
                      />
                      Currently employed here
                    </label>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">Duties</label>
                      <textarea
                        className="register-stage2-form__textarea"
                        value={record.duties}
                        onChange={(e) =>
                          handleRecordChange(
                            setEmploymentRecords,
                            index,
                            "duties",
                            e.target.value,
                          )
                        }
                        rows="2"
                        placeholder="Key responsibilities and achievements"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Employment evidence
                      </label>
                      <div className="register-stage2-form__file-upload">
                        <input
                          type="file"
                          id={`emp-file-${index}`}
                          className="register-stage2-form__file-input"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) =>
                            handleRecordFileChange(
                              setEmploymentRecords,
                              index,
                              e.target.files?.[0] || null,
                              "evidence_file",
                            )
                          }
                          disabled={loading}
                        />
                        <label
                          htmlFor={`emp-file-${index}`}
                          className="register-stage2-form__file-label"
                        >
                          <span className="register-stage2-form__file-icon">
                            📄
                          </span>
                          <span>{record.file_name || "Upload proof document"}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="register-stage2-add-btn"
              onClick={() => addRecord(setEmploymentRecords, createEmptyEmployment)}
              disabled={loading}
            >
              <FaPlus /> Add Employment
            </button>
            <div className="register-stage2-form__actions register-stage2-form__actions--inline">
              <button
                type="button"
                className="register-stage2-form__btn register-stage2-form__btn--primary"
                onClick={onSubmitEmployment}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Employment"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Section 10: Project Experience */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("project")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">📋</span>
              10. Project Experience
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.project ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Key projects and assignments.
          </p>
        </div>

        {expandedSections.project && (
          <>
            {projectRecords.map((record, index) => (
              <div
                key={record.id}
                className="register-stage2-record-card register-stage2-record-card--compact"
              >
                <div className="register-stage2-record-card__header">
                  <h4 className="register-stage2-record-card__title">
                    Project #{index + 1}
                  </h4>
                  {projectRecords.length > 1 && (
                    <button
                      type="button"
                      className="register-stage2-record-card__remove"
                      onClick={() => removeProjectRecord(index)}
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <div className="register-stage2-form__grid register-stage2-form__grid--4col">
                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Project name
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.project_name}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "project_name",
                          e.target.value,
                        )
                      }
                      placeholder="Project Name"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Client name
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.client_name}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "client_name",
                          e.target.value,
                        )
                      }
                      placeholder="Client Name"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Employer / contracting entity
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.employer}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "employer",
                          e.target.value,
                        )
                      }
                      placeholder="Employer"
                      disabled={loading}
                    />
                  </div>

                  {/* ROLE PERFORMED - Store ID instead of label */}
                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Role performed
                    </label>
                    <select
                      className="register-stage2-form__select"
                      value={record.role_id || ""}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "role_id",
                          e.target.value ? parseInt(e.target.value) : null,
                        )
                      }
                      disabled={loading}
                    >
                      <option value="">Select a role</option>
                      {professionalRoles.map((role) => (
                        <option key={role.id} value={role.id}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Location — country / site
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.location}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "location",
                          e.target.value,
                        )
                      }
                      placeholder="Country / site"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">Country</label>
                    <select
                      className="register-stage2-form__select"
                      value={record.country}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "country",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    >
                      <option value="">Country</option>
                      {COUNTRY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Start date
                    </label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.start_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "start_date",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">End date</label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.end_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "end_date",
                          e.target.value,
                        )
                      }
                      placeholder="End"
                      disabled={loading || record.is_current}
                    />
                    <small className="register-stage2-form__help-text">
                      Blank only if current
                    </small>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Allocation to project
                    </label>
                    <input
                      type="number"
                      className="register-stage2-form__input"
                      value={record.allocation_percent}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "allocation_percent",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      placeholder="1–100%"
                      min="0"
                      max="100"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Verified field days
                    </label>
                    <input
                      type="number"
                      className="register-stage2-form__input"
                      value={record.verified_field_days}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "verified_field_days",
                          parseInt(e.target.value) || 0,
                        )
                      }
                      placeholder="optional"
                      min="0"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={record.is_current}
                        onChange={(e) =>
                          handleRecordChange(
                            setProjectRecords,
                            index,
                            "is_current",
                            e.target.checked,
                          )
                        }
                        disabled={loading}
                      />
                      Current project
                    </label>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__checkbox-label">
                      <input
                        type="checkbox"
                        checked={record.is_primary}
                        onChange={(e) =>
                          handleRecordChange(
                            setProjectRecords,
                            index,
                            "is_primary",
                            e.target.checked,
                          )
                        }
                        disabled={loading}
                      />
                      Primary assignment
                    </label>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Codes / standards
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={(record.codes_standards || []).join(", ")}
                      onChange={(e) => {
                        const values = e.target.value
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean);
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "codes_standards",
                          values,
                        );
                      }}
                      placeholder="API 5L, ISO 21809"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Responsibilities
                      </label>
                      <textarea
                        className="register-stage2-form__textarea"
                        value={record.responsibilities}
                        onChange={(e) =>
                          handleRecordChange(
                            setProjectRecords,
                            index,
                            "responsibilities",
                            e.target.value,
                          )
                        }
                        rows="2"
                        placeholder="Responsibilities and duties"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Key achievements
                      </label>
                      <textarea
                        className="register-stage2-form__textarea"
                        value={record.achievements}
                        onChange={(e) =>
                          handleRecordChange(
                            setProjectRecords,
                            index,
                            "achievements",
                            e.target.value,
                          )
                        }
                        rows="2"
                        placeholder="Key achievements"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Project evidence
                      </label>
                      <div className="register-stage2-form__file-upload">
                        <input
                          type="file"
                          id={`proj-file-${index}`}
                          className="register-stage2-form__file-input"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) =>
                            handleRecordFileChange(
                              setProjectRecords,
                              index,
                              e.target.files?.[0] || null,
                              "evidence_file",
                            )
                          }
                          disabled={loading}
                        />
                        <label
                          htmlFor={`proj-file-${index}`}
                          className="register-stage2-form__file-label"
                        >
                          <span className="register-stage2-form__file-icon">
                            📄
                          </span>
                          <span>
                            {record.file_name || "Upload project evidence"}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Client visibility
                    </label>
                    <select
                      className="register-stage2-form__select"
                      value={record.client_visibility ?? "SHOW_CLIENT_NAME"}
                      onChange={(e) =>
                        handleRecordChange(
                          setProjectRecords,
                          index,
                          "client_visibility",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    >
                      <option value="SHOW_CLIENT_NAME">Show client name</option>
                      <option value="SHOW_MASKED_CLIENT_CATEGORY">
                        Show masked client category
                      </option>
                      <option value="CONFIDENTIAL_DO_NOT_DISPLAY">
                        Confidential — do not display
                      </option>
                    </select>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">Industry</label>
                    <select
                      className="register-stage2-form__select"
                      value={record.industry_id ?? ""}
                      onChange={(e) => handleIndustryChange(index, e.target.value)}
                      disabled={loading}
                    >
                      <option value="">Select industry</option>
                      {getIndustryOptions().map((industry) => (
                        <option key={industry.id} value={industry.id}>
                          {industry.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Scope categories
                      </label>
                      {record.industry_id ? (
                        <div className="register-stage2-form__checkbox-grid">
                          {getScopesForIndustry(record.industry_id).map((scope) => (
                            <label
                              key={scope.id}
                              className="register-stage2-form__checkbox-label"
                            >
                              <input
                                type="checkbox"
                                checked={
                                  Array.isArray(record.scope_ids) &&
                                  record.scope_ids.includes(scope.id)
                                }
                                onChange={(e) =>
                                  handleScopeToggle(
                                    index,
                                    scope.id,
                                    e.target.checked,
                                  )
                                }
                                disabled={loading}
                              />
                              {scope.scope_name ||
                                scope.name ||
                                `Scope ${scope.id}`}
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="register-stage2-form__help-text">
                          Choose an industry to load scope values.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col register-stage2-scope-fields-wrapper">
                    {Array.isArray(record.scope_ids) &&
                      record.scope_ids.length > 0 && (
                        <div className="register-stage2-form__field-group register-stage2-scope-fields">
                          <label className="register-stage2-form__label">
                            Scope-specific details
                          </label>
                          {record.scope_ids.map((scopeId) => {
                            const scopeFields = getFieldsForScope(scopeId);
                            return (
                              <div
                                key={scopeId}
                                className="register-stage2-scope-card"
                              >
                                <strong>
                                  {getScopeLabel(scopeId) || `Scope ${scopeId}`}
                                </strong>
                                {scopeFields.length > 0 ? (
                                  scopeFields.map((field, fIdx) => (
                                    <div
                                      key={field.id}
                                      className="register-stage2-form__field-group register-stage2-form__field-group--nested"
                                    >
                                      <label className="register-stage2-form__label">
                                        <span className="register-stage2-field-number">
                                          {fIdx + 1}.
                                        </span>
                                        {field.field_label}
                                      </label>
                                      {renderScopeField(
                                        record,
                                        index,
                                        scopeId,
                                        field,
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <p className="register-stage2-form__help-text">
                                    No dynamic form fields configured for this
                                    scope.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="register-stage2-add-btn"
              onClick={() => addRecord(setProjectRecords, createEmptyProject)}
              disabled={loading}
            >
              <FaPlus /> Add Project
            </button>
            <div className="register-stage2-form__actions register-stage2-form__actions--inline">
              <button
                type="button"
                className="register-stage2-form__btn register-stage2-form__btn--primary"
                onClick={onSubmitProject}
                disabled={loading}
              >
                {loading ? "Submitting..." : "Submit Project"}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Section 12: Client Approvals */}
      <div className="register-stage2-section">
        <div
          className="register-stage2-section__header"
          onClick={() => toggleSection("clientApproval")}
        >
          <div className="register-stage2-section__header-top">
            <h3 className="register-stage2-section__title">
              <span className="register-stage2-section__icon">✅</span>
              12. Client Approvals
            </h3>
            <span className="register-stage2-section__chevron">
              {expandedSections.clientApproval ? <FaChevronUp /> : <FaChevronDown />}
            </span>
          </div>
          <p className="register-stage2-section__subtitle">
            Client-specific approvals and authorizations.
          </p>
        </div>

        {expandedSections.clientApproval && (
          <>
            {clientApprovalRecords.map((record, index) => (
              <div key={record.id} className="register-stage2-record-card">
                <div className="register-stage2-record-card__header">
                  <h4 className="register-stage2-record-card__title">
                    Client / agency approval #{index + 1}
                  </h4>
                  {clientApprovalRecords.length > 1 && (
                    <button
                      type="button"
                      className="register-stage2-record-card__remove"
                      onClick={() =>
                        removeRecord(
                          setClientApprovalRecords,
                          index,
                          createEmptyClientApproval,
                        )
                      }
                      disabled={loading}
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <div className="register-stage2-form__grid register-stage2-form__grid--4col">
                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Client / agency name
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.client_name}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "client_name",
                          e.target.value,
                        )
                      }
                      placeholder="Client / agency name"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Role / scope approved for
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.role_scope_approved_for}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "role_scope_approved_for",
                          e.target.value,
                        )
                      }
                      placeholder="Role / scope approved for"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Related project
                    </label>
                    <select
                      className="register-stage2-form__select"
                      value={record.related_project || ""}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "related_project",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    >
                      <option value="">Select a project</option>
                      {projectOptions.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Approval number
                    </label>
                    <input
                      type="text"
                      className="register-stage2-form__input"
                      value={record.approval_number}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "approval_number",
                          e.target.value,
                        )
                      }
                      placeholder="Approval number"
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Approval date
                    </label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.approval_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "approval_date",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">
                      Expiry date
                    </label>
                    <input
                      type="date"
                      className="register-stage2-form__input"
                      value={record.expiry_date}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "expiry_date",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    />
                  </div>

                  <div className="register-stage2-form__field-group">
                    <label className="register-stage2-form__label">Status</label>
                    <select
                      className="register-stage2-form__select"
                      value={record.status}
                      onChange={(e) =>
                        handleRecordChange(
                          setClientApprovalRecords,
                          index,
                          "status",
                          e.target.value,
                        )
                      }
                      disabled={loading}
                    >
                      {APPROVAL_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="register-stage2-form__grid register-stage2-form__grid--1col">
                    <div className="register-stage2-form__field-group">
                      <label className="register-stage2-form__label">
                        Evidence
                      </label>
                      <div className="register-stage2-form__file-upload">
                        <input
                          type="file"
                          id={`approval-file-${index}`}
                          className="register-stage2-form__file-input"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) =>
                            handleRecordFileChange(
                              setClientApprovalRecords,
                              index,
                              e.target.files?.[0] || null,
                              "evidence_file",
                            )
                          }
                          disabled={loading}
                        />
                        <label
                          htmlFor={`approval-file-${index}`}
                          className="register-stage2-form__file-label"
                        >
                          <span className="register-stage2-form__file-icon">
                            📄
                          </span>
                          <span>{record.file_name || "Upload evidence"}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="register-stage2-add-btn"
              onClick={() =>
                addRecord(setClientApprovalRecords, createEmptyClientApproval)
              }
              disabled={loading}
            >
              <FaPlus /> Add client / agency approval
            </button>
          </>
        )}
      </div>

      <div className="register-stage2-form__actions">
        <button
          type="button"
          className="register-stage2-form__btn register-stage2-form__btn--outline"
          onClick={onBack}
          disabled={loading}
        >
          ← Previous Tab
        </button>
        <button
          type="button"
          className="register-stage2-form__btn register-stage2-form__btn--primary"
          onClick={onSubmit}
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit & Continue"}
        </button>
      </div>
    </div>
  );
};

export default Tab3Experience;